// src/pages/onboarding/shared/steps/StepName.jsx
import React, { useEffect, useMemo, useState } from "react";
import Button from "@/components/ui/Button";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import DoctorSalutationDropdown from "@/pages/shared/DoctorSalutationDropdown.jsx";
import { CircleX as CircleXIcon } from "lucide-react";
import supabase from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useLocation } from "react-router-dom";
import { readLastRole } from "@/lib/roleCache";
import { canUseOnboardingDraftMode } from "@/lib/onboardingDraftMode";

import {
  loadPatientIntroPreview,
  savePatientIntroPreview,
} from "@/lib/patientIntroPreview";
import {
  loadDoctorIntroPreview,
  saveDoctorIntroPreview,
} from "@/lib/doctorIntroPreview";

const DOCTOR_SALUTATION_OPTIONS = ["", "Dr.", "Prof.", "Rn.", "Mr.", "Mme."];
const PATIENT_SALUTATION_OPTIONS = ["", "Mr.", "Mrs.", "Ms.", "Miss", "Mme.", "Prof."];

function withTimeout(promise, ms = 12000, label = "Request") {
  return Promise.race([
    promise,
    new Promise((_, rej) =>
      window.setTimeout(() => rej(new Error(`${label} timed out`)), ms)
    ),
  ]);
}

const ALLOWED_PROFILE_TABLES = new Set(["patient_profiles", "doctor_profiles"]);

function resolveProfileTable({ passed, pathname, userId }) {
  // 1) if explicitly passed and valid, respect it
  if (typeof passed === "string" && ALLOWED_PROFILE_TABLES.has(passed)) return passed;

  // 2) infer from URL
  if (String(pathname || "").includes("/onboarding/doctor")) return "doctor_profiles";
  if (String(pathname || "").includes("/onboarding/patient")) return "patient_profiles";

  // 3) fallback from persisted role (used during auth round-trips).
  const lastRole = readLastRole(userId);
  if (lastRole === "doctor") return "doctor_profiles";
  if (lastRole === "patient") return "patient_profiles";

  // 4) no safe inference possible.
  return "";
}

export default function StepName({
  step,
  total,
  first = "",
  last = "",
  salutation = "",
  onChangeFirst,
  onChangeLast,
  onChangeSalutation,
  onBack,
  onContinue,
  profileTable: profileTableProp,
  contextNote = "",
  onTelemetry,
}) {
  const { refreshProfile, user } = useAuth() || {};
  const { t } = useLanguage();
  const { pathname } = useLocation();

  const profileTable = useMemo(
    () => resolveProfileTable({ passed: profileTableProp, pathname, userId: user?.id || "" }),
    [profileTableProp, pathname, user?.id]
  );
  const isDoctorProfile = profileTable === "doctor_profiles";
  const isPatientProfile = profileTable === "patient_profiles";

  const [firstLocal, setFirstLocal] = useState(typeof first === "string" ? first : "");
  const [lastLocal, setLastLocal] = useState(typeof last === "string" ? last : "");
  const [salutationLocal, setSalutationLocal] = useState(
    isDoctorProfile
      ? (DOCTOR_SALUTATION_OPTIONS.includes(salutation) ? salutation : "")
      : (PATIENT_SALUTATION_OPTIONS.includes(salutation) ? salutation : "")
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const activeSalutationOptions = isDoctorProfile ? DOCTOR_SALUTATION_OPTIONS : PATIENT_SALUTATION_OPTIONS;
  const salutationOptions = useMemo(
    () =>
      activeSalutationOptions.map((option) => ({
        value: option,
        label: option ? t(`onboarding.name.salutation.${option}`, option) : t("onboarding.name.salutation.none", "No title"),
      })),
    [activeSalutationOptions, t]
  );

  useEffect(() => {
    if (typeof first === "string") setFirstLocal(first);
  }, [first]);

  useEffect(() => {
    if (typeof last === "string") setLastLocal(last);
  }, [last]);

  useEffect(() => {
    if (isDoctorProfile && DOCTOR_SALUTATION_OPTIONS.includes(salutation)) {
      setSalutationLocal(salutation);
    } else if (isDoctorProfile) {
      setSalutationLocal("");
    } else if (isPatientProfile && PATIENT_SALUTATION_OPTIONS.includes(salutation)) {
      setSalutationLocal(salutation);
    }
  }, [isDoctorProfile, isPatientProfile, salutation]);

  const valid = useMemo(
    () =>
      firstLocal.trim().length > 0 &&
      lastLocal.trim().length > 0 &&
      (!isDoctorProfile || DOCTOR_SALUTATION_OPTIONS.includes(salutationLocal)) &&
      (!isPatientProfile || PATIENT_SALUTATION_OPTIONS.includes(salutationLocal)),
    [firstLocal, isDoctorProfile, isPatientProfile, lastLocal, salutationLocal]
  );

  function persistToPreview(nextFirst, nextLast, nextSalutation = salutationLocal) {
    try {
      const f = String(nextFirst || "").trim();
      const l = String(nextLast || "").trim();
      const full = `${f} ${l}`.trim();

      if (isDoctorProfile) {
        const current = loadDoctorIntroPreview(user?.id || "") || {};
        saveDoctorIntroPreview({
          ...current,
          salutation: nextSalutation,
          firstName: f,
          lastName: l,
          name: full,
          full_name: full,
        }, user?.id || "");
        return;
      }

      const current = loadPatientIntroPreview(user?.id || "") || {};
      savePatientIntroPreview({
        ...current,
        salutation: PATIENT_SALUTATION_OPTIONS.includes(nextSalutation) ? nextSalutation : "",
        firstName: f,
        lastName: l,
        name: full,
        full_name: full,
      }, user?.id || "");
    } catch {
      /* silent */
    }
  }

  async function requireUserId() {
    const { data, error } = await supabase.auth.getUser();
    const uid = data?.user?.id || null;
    if (error || !uid) {
      if (canUseOnboardingDraftMode()) return "";
      throw new Error(
        t(
          "onboarding.name.sessionNotReady",
          "Your session isn't ready yet. Please try again."
        )
      );
    }
    return uid;
  }

  async function commitNameToDb(firstName, lastName, selectedSalutation) {
    const uid = await requireUserId();
    if (!uid) return;

    if (!ALLOWED_PROFILE_TABLES.has(profileTable)) {
      throw new Error(`Invalid profile table: ${String(profileTable)}`);
    }

    const f = String(firstName || "").trim();
    const l = String(lastName || "").trim();
    const full = `${f} ${l}`.trim();

    const payload = {
      user_id: uid,
      first_name: f,
      last_name: l,
      full_name: full,
      updated_at: new Date().toISOString(),
    };
    if (isDoctorProfile) payload.salutation = selectedSalutation || null;
    if (isPatientProfile) payload.salutation = selectedSalutation || null;

    // ✅ IMPORTANT: upsert only (no returning read)
    const { error } = await withTimeout(
      supabase.from(profileTable).upsert(payload, { onConflict: "user_id" }),
      12000,
      t("onboarding.name.saveRequest", "Save name")
    );

    if (error) throw error;
  }

  async function commitAndContinue() {
    const f = firstLocal.trim();
    const l = lastLocal.trim();
    const selectedSalutation = isDoctorProfile ? salutationLocal : (PATIENT_SALUTATION_OPTIONS.includes(salutationLocal) ? salutationLocal : "");
    onTelemetry?.("continue_clicked", { valid, saving });
    if (
      !f ||
      !l ||
      (isDoctorProfile && !DOCTOR_SALUTATION_OPTIONS.includes(selectedSalutation)) ||
      (isPatientProfile && !PATIENT_SALUTATION_OPTIONS.includes(selectedSalutation)) ||
      saving
    ) {
      onTelemetry?.("validation_blocked", { reason: saving ? "saving" : "missing_name" });
      return;
    }

    try {
      setSaving(true);
      setErr("");
      onTelemetry?.("save_started");

      await commitNameToDb(f, l, selectedSalutation);
      onTelemetry?.("save_succeeded");
      persistToPreview(f, l, selectedSalutation);

      // fire and forget
      if (typeof refreshProfile === "function") {
        try {
          refreshProfile();
        } catch {}
      }

      onContinue?.();
    } catch (e) {
      console.error("StepName Save Error:", e);
      onTelemetry?.("save_failed", { reason: "name_save_failed" });
      setErr(
        e?.message ||
          t("onboarding.name.saveError", "Could not save. Please try again.")
      );
    } finally {
      setSaving(false);
    }
  }

  function onEnter(e) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (valid && !saving) commitAndContinue();
  }

  return (
    <>
      <OnboardingTopBar step={step} total={total} onBack={onBack} />

      <OnboardingSection
        header={
          <div className="space-y-2">
            <h2 className="text-title-large text-on-surface">
              {t("onboarding.name.title", "Your full names")}
            </h2>
            <p className="text-body-medium text-on-surface-var">
              {t(
                "onboarding.name.subtitle",
                "Make sure this matches the name on your official government issued ID. Select the correct title to be addressed by."
              )}
            </p>
            {contextNote ? (
              <p className="text-body-small text-on-surface-var">{contextNote}</p>
            ) : null}
          </div>
        }
        footer={
          <Button size="lg" disabled={!valid || saving} onClick={commitAndContinue}>
            <span className="text-title-medium">
              {saving
                ? t("onboarding.name.saving", "Saving...")
                : t("onboarding.name.continue", "Continue")}
            </span>
          </Button>
        }
      >
        <div className="space-y-4">
          {isDoctorProfile || isPatientProfile ? (
            <DoctorSalutationDropdown
              id={isDoctorProfile ? "onboarding-doctor-salutation" : "onboarding-patient-salutation"}
              name="salutation"
              label={t("onboarding.name.salutationLabel", "Title")}
              placeholder={
                isDoctorProfile
                  ? t("onboarding.name.salutationPlaceholder", "Select title")
                  : t("onboarding.name.patientSalutationPlaceholder", "Optional")
              }
              value={salutationLocal}
              options={salutationOptions}
              required={isDoctorProfile}
              tone="surface"
              onChange={(e) => {
                const next = e.target.value;
                setSalutationLocal(next);
                setErr("");
                onTelemetry?.("field_changed", { field: "salutation", value_recorded: false });
                onChangeSalutation?.(next);
                persistToPreview(firstLocal, lastLocal, next);
              }}
            />
          ) : null}

          {/* First Name */}
          <div className="relative">
            <TextField56
              name="firstName"
              label={t("onboarding.name.firstLabel", "First Name")}
              value={firstLocal}
              onChange={(e) => {
                const val = e?.target ? e.target.value : e;
                const next = String(val || "");
                setFirstLocal(next);
                setErr("");
                onTelemetry?.("field_changed", { field: "first_name", value_recorded: false });
                onChangeFirst?.(next);
                persistToPreview(next, lastLocal);
              }}
              onKeyDown={onEnter}
              placeholder={t(
                "onboarding.name.firstPlaceholder",
                "First Name/s on ID"
              )}
              autoComplete="given-name"
              tone="surface"
            />
            {firstLocal?.length > 0 && (
              <button
                type="button"
                aria-label={t("onboarding.name.clearFirst", "Clear first name")}
                onClick={() => {
                  setFirstLocal("");
                  setErr("");
                  onChangeFirst?.("");
                  persistToPreview("", lastLocal);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 8,
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <CircleXIcon className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant))]" />
              </button>
            )}
          </div>

          {/* Last Name */}
          <div className="relative">
            <TextField56
              name="lastName"
              label={t("onboarding.name.lastLabel", "Family name on ID")}
              value={lastLocal}
              onChange={(e) => {
                const val = e?.target ? e.target.value : e;
                const next = String(val || "");
                setLastLocal(next);
                setErr("");
                onTelemetry?.("field_changed", { field: "last_name", value_recorded: false });
                onChangeLast?.(next);
                persistToPreview(firstLocal, next);
              }}
              onKeyDown={onEnter}
              placeholder={t(
                "onboarding.name.lastPlaceholder",
                "Family Name/s on ID"
              )}
              autoComplete="family-name"
              tone="surface"
            />
            {lastLocal?.length > 0 && (
              <button
                type="button"
                aria-label={t("onboarding.name.clearLast", "Clear family name")}
                onClick={() => {
                  setLastLocal("");
                  setErr("");
                  onChangeLast?.("");
                  persistToPreview(firstLocal, "");
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 8,
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <CircleXIcon className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant))]" />
              </button>
            )}
          </div>

          {!!err && (
            <p className="text-body-medium text-[rgb(var(--afrimed-danger))]">{err}</p>
          )}
        </div>
      </OnboardingSection>
    </>
  );
}
